import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Globe, LogOut, LayoutDashboard, Heart } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function LanguageToggle() {
  const { lang, toggle } = useLang();
  return (
    <button
      onClick={toggle}
      data-testid="language-toggle"
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-500/10 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-600"
      aria-label={lang === "hi" ? "Switch to English" : "हिन्दी में बदलें"}
    >
      <Globe className="h-4 w-4" />
      {lang === "hi" ? "English" : "हिन्दी"}
    </button>
  );
}

export default function Navbar() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-amber-500/20 toran-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 min-w-0" data-testid="brand-home-link">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-[#0A2540] text-[#D4AF37] shadow-md shrink-0">
            <Heart className="h-4.5 w-4.5" fill="currentColor" />
          </span>
          <span className="flex flex-col leading-tight min-w-0">
            <span className="font-serif text-lg text-[#8B1E2B] truncate">101 {t("club_name") === "लायंस क्लब जयपुर हवामहल" ? "कन्या विवाह" : "Kanya Vivah"}</span>
            <span className="text-[10px] uppercase tracking-widest text-amber-700 truncate">{t("club_name")}</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1 text-sm">
          <a href="/#about" className="px-3 py-2 rounded-md text-slate-700 hover:text-[#8B1E2B] hover:bg-amber-500/10 transition-colors" data-testid="nav-about">{t("nav_about")}</a>
          <a href="/#process" className="px-3 py-2 rounded-md text-slate-700 hover:text-[#8B1E2B] hover:bg-amber-500/10 transition-colors" data-testid="nav-process">{t("nav_process")}</a>
          <a href="/#eligibility" className="px-3 py-2 rounded-md text-slate-700 hover:text-[#8B1E2B] hover:bg-amber-500/10 transition-colors" data-testid="nav-eligibility">{t("nav_eligibility")}</a>
          <Link to="/honour" className="px-3 py-2 rounded-md text-slate-700 hover:text-[#8B1E2B] hover:bg-amber-500/10 transition-colors" data-testid="nav-gallery">{t("honour_gallery")}</Link>
          <a href="/#contact" className="px-3 py-2 rounded-md text-slate-700 hover:text-[#8B1E2B] hover:bg-amber-500/10 transition-colors" data-testid="nav-contact">{t("nav_contact")}</a>
        </div>

        <div className="flex items-center gap-2">
          <LanguageToggle />
          {!user && (
            <>
              <Link to="/login" className="hidden sm:block" data-testid="nav-login">
                <Button variant="ghost" className="text-[#8B1E2B]">{t("login")}</Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="bg-[#8B1E2B] hover:bg-[#6f1622] text-white">{t("register")}</Button>
              </Link>
            </>
          )}
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-amber-500/40" data-testid="user-menu-trigger">
                  <Menu className="h-4 w-4 mr-1.5" />
                  <span className="max-w-[120px] truncate">{user.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {user.role === "admin" ? (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> {t("nav_admin")}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> {t("nav_dashboard")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={doLogout} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
